/**
 * @kantera/ui - CSS-in-JS Utilities
 * Style helper functions and common mixins
 */
import { theme } from './theme.js';
/**
 * Merge multiple style objects into one
 */
export function css(...styles) {
    return Object.assign({}, ...styles.filter(Boolean));
}
/**
 * Create a flex center layout
 */
export const flexCenter = () => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});
/**
 * Create a flex column layout
 */
export const flexCol = (gap) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: typeof gap === 'number' ? `${gap}px` : gap || theme.spacing.md,
});
/**
 * Create a flex row layout
 */
export const flexRow = (gap) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: typeof gap === 'number' ? `${gap}px` : gap || theme.spacing.md,
});
/**
 * Truncate text with ellipsis
 */
export const truncate = () => ({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});
/**
 * Truncate text to N lines
 */
export const lineClamp = (lines) => ({
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
});
/**
 * Glass morphism effect - frosted glass surface
 */
export const glassSurface = () => ({
    backgroundColor: theme.colors.overlay.light,
    backdropFilter: 'blur(10px)',
    border: `1px solid ${theme.colors.border.secondary}`,
});
/**
 * Glass morphism effect - stronger backdrop blur
 */
export const glassStrong = () => ({
    backgroundColor: theme.colors.glass.strong,
    backdropFilter: 'blur(16px)',
    border: `1px solid ${theme.colors.border.secondary}`,
});
/**
 * Dark surface with subtle border
 */
export const darkSurface = (borderColor) => ({
    backgroundColor: theme.colors.bg.secondary,
    border: `1px solid ${borderColor || theme.colors.border.secondary}`,
});
/**
 * Smooth focus ring for accessibility
 */
export const focusRing = () => ({
    outline: `3px solid ${theme.colors.accent.primary}`,
    outlineOffset: '2px',
    boxShadow: `0 0 0 3px ${theme.colors.accent.rgbA(0.2)}, 0 0 0 6px ${theme.colors.accent.primary}`,
});
/**
 * Smooth focus ring (subtle)
 */
export const focusRingSubtle = () => ({
    outline: `3px solid ${theme.colors.accent.primary}`,
    outlineOffset: '2px',
    boxShadow: `0 0 0 2px ${theme.colors.accent.rgbA(0.1)}, 0 0 0 4px ${theme.colors.accent.primary}`,
});
/**
 * Gradient background - indigo to green
 */
export const gradientAccent = () => ({
    background: `linear-gradient(135deg, ${theme.colors.accent.primary} 0%, ${theme.colors.success.primary} 100%)`,
});
/**
 * Gradient background - dark to darker
 */
export const gradientDark = () => ({
    background: `linear-gradient(135deg, ${theme.colors.bg.secondary} 0%, ${theme.colors.bg.tertiary} 100%)`,
});
/**
 * Smooth transition for all properties
 */
export const smoothTransition = (duration = 'base') => ({
    transition: theme.transitions[duration],
});
/**
 * Absolutely centered element
 */
export const absoluteCenter = () => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
});
/**
 * Full coverage overlay
 */
export const fullOverlay = () => ({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: theme.zIndex.modal,
});
/**
 * Responsive grid layout
 */
export const responsiveGrid = (minWidth = '240px', gap) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fill, minmax(${minWidth}, 1fr))`,
    gap: gap || theme.spacing.lg,
});
/**
 * Base button reset styles
 */
export const buttonReset = () => ({
    appearance: 'none',
    border: 'none',
    background: 'none',
    padding: 0,
    margin: 0,
    font: 'inherit',
    cursor: 'pointer',
    outline: 'none',
});
/**
 * Base input reset styles
 */
export const inputReset = () => ({
    appearance: 'none',
    border: 'none',
    background: 'transparent',
    padding: 0,
    margin: 0,
    font: 'inherit',
    color: 'inherit',
    outline: 'none',
    boxShadow: 'none',
});
/**
 * Hide scrollbar but keep scroll functionality.
 * Covers Firefox and Chrome 121+ via scrollbarWidth.
 * Older webkit browsers require a companion CSS rule:
 *   ::-webkit-scrollbar { display: none }
 * That rule cannot be expressed as an inline CSSProperties value.
 */
export const hideScrollbar = () => ({
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
});
/**
 * Scrollbar styling for webkit browsers
 */
export const styledScrollbar = () => ({
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.colors.accent.primary} ${theme.colors.bg.tertiary}`,
});
/**
 * Shadow elevation levels
 */
export const elevation = (level = 'md') => ({
    boxShadow: theme.shadows[level],
});
/**
 * Disabled state for interactive elements
 */
export const disabledState = () => ({
    opacity: 0.6,
    cursor: 'not-allowed',
    pointerEvents: 'none',
});
/**
 * Loading animation keyframes (returns CSS text for <style> tag)
 */
export const spinnerKeyframes = `
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;
/**
 * Pulse animation keyframes
 */
export const pulseKeyframes = `
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;
/**
 * Shimmer animation keyframes
 */
export const shimmerKeyframes = `
  @keyframes shimmer {
    0% {
      backgroundPosition: -1000px 0;
    }
    100% {
      backgroundPosition: 1000px 0;
    }
  }
`;
/**
 * Gradient shift animation keyframes
 */
export const gradientShiftKeyframes = `
  @keyframes gradientShift {
    0%, 100% {
      backgroundPosition: 0% 50%;
    }
    50% {
      backgroundPosition: 100% 50%;
    }
  }
`;
/**
 * Slide in from left
 */
export const slideInLeftKeyframes = `
  @keyframes slideInLeft {
    from {
      transform: translateX(-100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;
/**
 * Fade in animation
 */
export const fadeInKeyframes = `
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;
/**
 * Common animation styles
 */
export const animations = {
    spin: {
        animation: 'spin 1s linear infinite',
    },
    pulse: {
        animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
    },
    slideInLeft: {
        animation: 'slideInLeft 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    },
    fadeIn: {
        animation: 'fadeIn 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    },
};
// ===== Elevated helpers (CSS-variable-backed, theme-switchable) =====
/** Expo.out easing — fast start, gentle settle. The premium curve. */
export const EASE_OUT_EXPO = 'cubic-bezier(0.16, 1, 0.3, 1)';
/** Spring overshoot easing — for hover lifts and tab slides. */
export const EASE_SPRING = 'cubic-bezier(0.34, 1.56, 0.64, 1)';
/** Cinematic page header gradient band. */
export function heroGradientElevated(_accentVar = 'var(--k-accent)') {
    return {
        background: `linear-gradient(135deg, var(--k-bg-deep) 0%, var(--k-bg-card) 50%, rgba(var(--k-accent-rgb), 0.03) 100%)`,
        borderBottom: '1px solid var(--k-border)',
        position: 'relative',
        overflow: 'hidden',
    };
}
/** Interactive card surface with hover lift + glow. */
export function cardSurfaceElevated(hovered = false) {
    return {
        backgroundColor: hovered ? 'var(--k-bg-elevated)' : 'var(--k-bg-card)',
        border: `1px solid ${hovered ? 'var(--k-line-soft)' : 'var(--k-border)'}`,
        // Mockup .surface: 18px radius + inset hair-line shadow
        borderRadius: theme.radius.card,
        boxShadow: hovered
            ? `${theme.shadows.raised}, 0 0 20px var(--k-accent-glow)`
            : theme.shadows.surface,
        transform: hovered ? 'translateY(-1px)' : 'translateY(0)',
        transition: `all 200ms ${EASE_SPRING}`,
    };
}
/** Brand-accent card variant (emerald glow on bottom edge). */
export function brandCardSurface(hovered = false) {
    return {
        ...cardSurfaceElevated(hovered),
        borderBottom: '2px solid var(--k-brand)',
        boxShadow: hovered
            ? `${theme.shadows.lg}, 0 4px 20px var(--k-brand-glow-strong)`
            : `${theme.shadows.sm}, 0 2px 10px var(--k-brand-glow)`,
    };
}
/** Frosted glass panel (backdrop-filter). */
export function glassPanel() {
    return {
        backgroundColor: 'var(--k-glass)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid var(--k-border)',
        borderRadius: theme.radius.lg,
    };
}
/** Strong glass (for modals, overlays). */
export function glassPanelStrong() {
    return {
        backgroundColor: 'var(--k-glass-strong)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border: '1px solid var(--k-border)',
        borderRadius: theme.radius.lg,
    };
}
/** Glass header bar (sticky nav/table headers). */
export function glassHeaderElevated() {
    return {
        backgroundColor: 'var(--k-glass)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        borderBottom: '1px solid var(--k-border)',
        position: 'sticky',
        top: 0,
        zIndex: theme.zIndex.sticky,
    };
}
/** Decorative animated orb (for hero headers). */
export function orbStyleElevated(size, color, positionX, positionY) {
    return {
        position: 'absolute',
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
        left: positionX,
        top: positionY,
        pointerEvents: 'none',
        animation: 'kPulseGlow 4s ease-in-out infinite',
        opacity: 0.6,
    };
}
/** Loading skeleton shimmer block. */
export function shimmerBlock(width, height) {
    return {
        width,
        height,
        borderRadius: theme.radius.sm,
        // backgroundImage: the shorthand resets background-size, so the line below only
        // takes effect because it comes second — reorder and the shimmer stops moving.
        backgroundImage: `linear-gradient(90deg, var(--k-bg-elevated) 0px, rgba(var(--k-brand-rgb), 0.06) 200px, var(--k-bg-elevated) 400px)`,
        backgroundSize: '800px 100%',
        animation: 'kShimmer 1.5s ease-in-out infinite',
    };
}
/** Pill-style filter/tag button. */
export function pillButtonElevated(active = false, accent = 'var(--k-accent)') {
    return {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '6px 14px',
        borderRadius: theme.radius.full,
        fontSize: theme.typography.fontSize.sm.size,
        fontWeight: theme.typography.fontWeight.medium,
        border: `1px solid ${active ? accent : 'var(--k-border)'}`,
        backgroundColor: active ? 'rgba(var(--k-accent-rgb), 0.08)' : 'transparent',
        color: active ? accent : 'var(--k-text-secondary)',
        cursor: 'pointer',
        transition: 'all 150ms ease',
        userSelect: 'none',
    };
}
/** Large stat number display. */
export const statNumberElevated = {
    // Mockup: large Space Grotesk display number, tight tracking
    fontFamily: 'var(--k-font-display)',
    fontSize: '28px',
    fontWeight: 600,
    letterSpacing: '-0.035em',
    color: 'var(--k-text-primary)',
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 0.95,
};
/** Mono-uppercase muted stat label (mockup eyebrow). */
export const statLabelElevated = {
    fontFamily: 'var(--k-font-mono)',
    fontSize: '10.5px',
    fontWeight: 500,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: 'var(--k-text-tertiary)',
    lineHeight: 1.4,
};
/** Mono-uppercase section header (mockup eyebrow). */
export const sectionTitleElevated = {
    fontFamily: 'var(--k-font-mono)',
    fontSize: '11px',
    fontWeight: 500,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: 'var(--k-text-tertiary)',
};
/** Full-bleed page wrapper. */
export function pageWrapElevated() {
    return {
        minHeight: '100vh',
        backgroundColor: 'var(--k-bg-base)',
        color: 'var(--k-text-primary)',
        fontFamily: theme.typography.fontFamily.primary,
    };
}
/** Form input base style. */
export function inputBase() {
    return {
        backgroundColor: 'var(--k-bg-input)',
        border: '1px solid var(--k-border)',
        borderRadius: theme.radius.md,
        color: 'var(--k-text-primary)',
        fontSize: theme.typography.fontSize.sm.size,
        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
        outline: 'none',
        transition: 'border-color 150ms ease, box-shadow 150ms ease',
        width: '100%',
    };
}
/** Input focus glow ring (brand green). */
export function inputFocusRing() {
    return {
        borderColor: 'var(--k-brand)',
        boxShadow: '0 0 0 3px var(--k-brand-glow)',
    };
}
/** Input error state. */
export function inputErrorRing() {
    return {
        borderColor: 'var(--k-danger)',
        boxShadow: '0 0 0 3px rgba(var(--k-danger-rgb), 0.15)',
    };
}
/** Micro-press animation style (apply on mousedown/active). */
export const microPress = {
    transform: 'scale(0.97)',
    transition: 'transform 80ms ease-in',
};
/** Release bounce (apply when not pressed). */
export const microRelease = {
    transform: 'scale(1)',
    transition: `transform 200ms ${EASE_SPRING}`,
};
//# sourceMappingURL=styles.js.map